import React, { FC, useCallback, useContext, useMemo } from 'react';
import { createFetcher, useAutoFetch } from '../../contexts/Fetcher';
import { apiProductRelease } from '../../resources/api/apiProductRelease';
import { useParams } from 'react-router-dom';
import { DataGrid, GridColumns, GridRowIdGetter, GridRowParams } from '@mui/x-data-grid';
import { FilesizeFormatter } from '../../valueFormatters/FilesizeValueFormatter';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { DateFormat } from '../../helpers/dateFormat';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../../filterOperators/dateFilterOperators';
import { getFileSizeGreaterThan, getFileSizeLessThan } from '../../filterOperators/fileSizeFilterOperators';
import { IAwsFileMetadata } from '../../resources/models/IAwsFileMetadata';
import { EasyFilter } from '../EasyFilter/EasyFilter';
import { useFreeTextFilter } from '../../hooks/useFreeTextFilter';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from '../../helpers/isAxiosError';
import { getInstallerVersion } from '../../helpers/getInstallerVersion';
import { useDialog } from '../../hooks/useDialog';
import { FilteringContextProvider } from '../../contexts/FilteringContext';

interface IUrlParams {
  productId: string;
}

const { useGetProductInstallers } = apiProductRelease;

const InstallersFetcher = createFetcher(() => {
  const { productId } = useParams<IUrlParams>();
  const { execute: getProductInstallers } = useGetProductInstallers(productId);
  return useCallback(async () => {
    try {
      const result = await getProductInstallers();
      return result;
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404) {
        return {
          product: void 0,
          files: [] as IAwsFileMetadata[],
        };
      }
      throw e;
    }
  }, [getProductInstallers, productId]);
});

const getId: GridRowIdGetter = (row) => row.Key;

interface IProps {
  onInstallerChange: (file: IAwsFileMetadata) => void;
}

const ProductInstallersContent: FC<IProps> = ({ onInstallerChange }) => {
  const { isOpen } = useDialog('new');
  useAutoFetch(InstallersFetcher, isOpen);
  const { data } = useContext(InstallersFetcher.Context);
  const { t } = useTranslation();

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'version',
        headerName: t('releases.addDialog.installer.version'),
        width: 60,
      },
      {
        field: 'Key',
        headerName: t('releases.addDialog.installer.fileName'),
        width: 350,
      },
      {
        field: 'LastModified',
        headerName: t('releases.addDialog.installer.modified'),
        width: 120,
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'Size',
        headerName: t('releases.addDialog.installer.size'),
        width: 120,
        valueFormatter: FilesizeFormatter,
        filterOperators: [getFileSizeLessThan(), getFileSizeGreaterThan()],
      },
    ],
    []
  );

  const onRowClick = useCallback(
    (params: GridRowParams) => {
      onInstallerChange(params.row as IAwsFileMetadata);
    },
    [onInstallerChange]
  );

  const filteredRows = useFreeTextFilter(data?.files ?? [], columns).map((row) => ({
    ...row,
    version: getInstallerVersion(row.Key),
  }));

  if (!data) return null;
  return (
    <>
      <EasyFilter />

      <DataGrid
        columns={columns}
        rows={filteredRows}
        getRowId={getId}
        autoHeight={true}
        pageSize={10}
        rowsPerPageOptions={[10]}
        onRowClick={onRowClick}
      />
    </>
  );
};

export const ProductInstallers: FC<IProps> = (props) => {
  return (
    <FilteringContextProvider>
      <InstallersFetcher.WMF>
        <ProductInstallersContent {...props} />
      </InstallersFetcher.WMF>
    </FilteringContextProvider>
  );
};
